import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchDataProcessingStatus, startDataProcessing } from '@/services/dataProcessingApi';
import { queryKeys } from '@/services/queryKeys';
import { DataProcessingJob } from '@/types/dataProcessing';

/**
 * The mutation key lets other components read "is a start in flight" via useIsMutating.
 * onSuccess is a hook option (not a mutate() option) so it still runs if the caller
 * unmounts before the request finishes.
 */
export const useStartDataProcessingMutation = (onSuccess: (job: DataProcessingJob) => void) =>
  useMutation({
    mutationKey: queryKeys.startDataProcessing,
    mutationFn: startDataProcessing,
    onSuccess,
  });

/**
 * Polls the Batch job status until it's complete (SUCCEEDED / FAILED), then stops.
 * Keeps polling while the tab is in the background so the result is ready when the
 * user comes back. Callers sharing a jobId share one cache entry and one poll.
 */
export const useDataProcessingStatusQuery = (jobId: string | null, userEmail: string | undefined, pollIntervalMs: number) =>
  useQuery({
    queryKey: queryKeys.dataProcessingStatus(jobId),
    queryFn: () => fetchDataProcessingStatus(jobId as string, userEmail as string),
    enabled: Boolean(jobId && userEmail),
    refetchInterval: (query) => (query.state.data?.isComplete ? false : pollIntervalMs),
    refetchIntervalInBackground: true,
    // A finished job never changes, so don't refetch it on focus/remount.
    staleTime: (query) => (query.state.data?.isComplete ? Infinity : 0),
  });
